import type { AgentSummary } from "@agentdash/shared";
import { useRepos } from "./hooks.js";
import { RepoCard } from "./RepoCard.js";
import { CronRow } from "./CronRow.js";
import { formatRelative } from "../../lib/time.js";
import { useNow } from "../../lib/use-now.js";

export function RepoList() {
  const q = useRepos();
  const now = useNow();

  if (q.isLoading) {
    return <div style={{ padding: 24, color: "var(--muted)" }}>Loading…</div>;
  }
  if (q.isError) {
    return (
      <div style={{ padding: 24, color: "var(--err)" }}>
        Failed to load repos: {String((q.error as Error).message ?? q.error)}
      </div>
    );
  }
  const data = q.data;
  if (!data) return null;

  return (
    <div>
      <header
        style={{
          padding: "12px 20px",
          color: "var(--muted)",
          fontSize: 12,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <span>{data.repos.length} repos</span>
        <span>•</span>
        <span>{data.ungrouped.cron_jobs.length} ungrouped jobs</span>
        <span>•</span>
        <span
          style={{
            display: "inline-flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {data.agents.length} agent{data.agents.length === 1 ? "" : "s"}:
          {data.agents.length === 0 ? (
            <span>none registered</span>
          ) : (
            data.agents.map((a) => (
              <AgentPill key={a.agent_id} agent={a} now={now} />
            ))
          )}
        </span>
      </header>

      {data.repos.length === 0 && data.ungrouped.cron_jobs.length === 0 && (
        <section className="panel" style={{ margin: 16, padding: 24 }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            No cron jobs reported yet
          </div>
          <div style={{ color: "var(--muted)", fontSize: 13 }}>
            Install the host agent on the machine that runs your cron jobs and
            give it ~30s to send its first snapshot. See{" "}
            <code>tools/host-agent/README.md</code> for the systemd install
            steps.
          </div>
        </section>
      )}

      {data.repos.map((repo) => (
        <RepoCard key={repo.repo_id} repo={repo} cronJobs={repo.cron_jobs} />
      ))}

      {data.ungrouped.cron_jobs.length > 0 && (
        <section className="panel" style={{ margin: 16 }}>
          <div className="panel-header">
            Ungrouped jobs ({data.ungrouped.cron_jobs.length})
            <span
              style={{
                marginLeft: 12,
                fontWeight: 400,
                color: "var(--muted)",
                fontSize: 12,
              }}
            >
              cron lines whose command isn't in any git repo
            </span>
          </div>
          {data.ungrouped.cron_jobs.map((j) => (
            <CronRow key={j.cron_job_id} job={j} />
          ))}
        </section>
      )}
    </div>
  );
}

function AgentPill({ agent, now }: { agent: AgentSummary; now: Date }) {
  const ageMs = agent.last_seen_at
    ? now.getTime() - new Date(agent.last_seen_at).getTime()
    : null;
  // Snapshot cadence is 30s; <60s = fresh, <5min = stale-but-recent, else stale/never.
  const tone =
    ageMs === null || ageMs >= 5 * 60_000
      ? "err"
      : ageMs < 60_000
      ? "accent"
      : "warn";
  const color = `var(--${tone})`;
  const label = agent.last_seen_at
    ? `seen ${formatRelative(agent.last_seen_at, now)}`
    : "never seen";
  return (
    <span
      title={
        agent.last_seen_at
          ? `Last snapshot from ${agent.hostname} at ${new Date(
              agent.last_seen_at,
            ).toLocaleString()}`
          : `${agent.hostname} has never reported a snapshot`
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        border: "1px solid var(--border)",
        borderRadius: 999,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 3,
          background: color,
          boxShadow: tone === "accent" ? `0 0 4px ${color}` : "none",
        }}
      />
      <span>{agent.hostname}</span>
      <span style={{ color }}>· {label}</span>
    </span>
  );
}
